<template>
  <div v-if="loading" class="loading-container">
    <v-progress-linear :indeterminate="true"></v-progress-linear>
  </div>
  <div v-else class="tenant-summary-dashboard">
    <div class="dashboard-header">
      <h2 class="display-1 mb-4">Podsumowanie Placówek</h2>
      <p class="subtitle-1 grey--text">Przegląd wszystkich Twoich placówek i ich aktywności</p>

      <!-- Global Legend -->
      <div class="global-legend-container mt-4">
        <div class="legend-item">
          <span class="legend-color legend-new"></span>
          <span class="legend-text">Nowe</span>
        </div>
        <div class="legend-item">
          <span class="legend-color legend-confirmed"></span>
          <span class="legend-text">Potwierdzone</span>
        </div>
        <div class="legend-item">
          <span class="legend-color legend-finished"></span>
          <span class="legend-text">Zakończone</span>
        </div>
        <div class="legend-item">
          <span class="legend-color legend-canceled"></span>
          <span class="legend-text">Anulowane</span>
        </div>
      </div>
    </div>

    <v-row v-if="tenantSummaries && tenantSummaries.length > 0" justify="center">
      <v-col
        v-for="tenant in tenantSummaries"
        :key="tenant.tenantId"
        cols="12"
        sm="6"
        md="4"
        lg="3"
      >
        <v-hover v-slot="{ hover }">
          <v-card
            :elevation="hover ? 12 : 2"
            class="tenant-card"
            @click="switchTenant(tenant.tenantId)"
          >
            <v-card-title class="card-header primary white--text">
              <div class="tenant-name">
                <v-icon left color="white">mdi-office-building</v-icon>
                {{ tenant.tenantName || 'Placówka' }}
              </div>
            </v-card-title>

            <v-card-text class="card-content">
              <!-- Visit Statistics -->
              <div class="stats-section mb-3">
                <div class="stats-label mb-2">
                  <v-icon small color="grey darken-1">mdi-chart-bar</v-icon>
                  <span class="ml-1 text-subtitle-2 grey--text text--darken-1 font-weight-bold">Podsumowanie wizyt</span>
                </div>
                <v-row dense>
                  <v-col cols="6" sm="3">
                    <div class="stat-box stat-new">
                      <div class="stat-value">{{ tenant.newVisitsCount || 0 }}</div>
                      <v-icon
                        v-if="tenant.hasWarning"
                        class="warning-icon"
                        color="orange"
                        small
                      >
                        fa-exclamation-triangle
                      </v-icon>
                    </div>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <div class="stat-box stat-confirmed">
                      <div class="stat-value">{{ tenant.confirmedVisitsCount || 0 }}</div>
                    </div>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <div class="stat-box stat-finished">
                      <div class="stat-value">{{ tenant.finishedVisitsCount || 0 }}</div>
                    </div>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <div class="stat-box stat-canceled">
                      <div class="stat-value">{{ tenant.canceledVisitsCount || 0 }}</div>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <!-- SMS Status (if available) -->
              <div v-if="tenant.smsDailyStatus" class="sms-section">
                <v-divider class="my-3"></v-divider>
                <div class="stats-label mb-2">
                  <v-icon small color="grey darken-1">mdi-message-text</v-icon>
                  <span class="ml-1 text-caption grey--text text--darken-1">
                    Status SMS ({{ formatDate(tenant.smsDailyStatus.forDay) }})
                  </span>
                </div>

                <div class="sms-progress mb-2">
                  <v-progress-linear
                    :value="calculateSMSProgress(tenant.smsDailyStatus)"
                    :color="getSMSProgressColor(tenant.smsDailyStatus)"
                    height="20"
                    rounded
                  >
                    <template v-slot:default>
                      <strong>{{ tenant.smsDailyStatus.smsSent }}/{{ tenant.smsDailyStatus.smsTotal }}</strong>
                    </template>
                  </v-progress-linear>
                </div>

                <v-row dense>
                  <v-col cols="6" v-if="tenant.smsDailyStatus.visitsToCheck > 0">
                    <v-chip x-small color="warning" text-color="white">
                      <v-icon x-small left>mdi-alert</v-icon>
                      Do sprawdzenia: {{ tenant.smsDailyStatus.visitsToCheck }}
                    </v-chip>
                  </v-col>
                  <v-col cols="6" v-if="tenant.smsDailyStatus.showError">
                    <v-chip x-small color="error" text-color="white">
                      <v-icon x-small left>mdi-alert-circle</v-icon>
                      Błąd SMS
                    </v-chip>
                  </v-col>
                </v-row>

                <div v-if="tenant.smsDailyStatus.lastTimeDeviceChecked" class="text-caption grey--text mt-2">
                  <v-icon x-small color="grey">mdi-clock-outline</v-icon>
                  Ostatnie sprawdzenie: {{ formatDateTime(tenant.smsDailyStatus.lastTimeDeviceChecked) }}
                </div>
              </div>
            </v-card-text>

            <v-card-actions class="card-actions justify-center">
              <v-btn
                color="success"
                dark
                @click.stop="switchTenant(tenant.tenantId)"
                class="px-8"
              >
                <v-icon left>fa-exchange-alt</v-icon>
                <span>Przełącz</span>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-hover>
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col cols="12">
        <v-alert
          type="info"
          outlined
          prominent
          border="left"
        >
          <v-row align="center">
            <v-col class="grow">
              Brak dostępnych placówek do wyświetlenia.
            </v-col>
          </v-row>
        </v-alert>
      </v-col>
    </v-row>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      top
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Zamknij
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { tenantService } from '@/services/tenant.service';
import { mapActions } from 'vuex';

export default {
  name: "TenantSummaryDashboard",
  data() {
    return {
      loading: true,
      tenantSummaries: [],
      refreshInterval: null,
      snackbar: {
        show: false,
        message: '',
        color: 'info'
      }
    };
  },
  methods: {
    ...mapActions("title", ["updateTitle"]),

    async loadTenantSummaries() {
      this.loading = true;
      try {
        const response = await tenantService.getTenantsVisitSummary();
        if (response.data && response.data.result) {
          this.tenantSummaries = response.data.result;
        } else {
          this.tenantSummaries = [];
        }
      } catch (error) {
        console.error('Error loading tenant summaries:', error);
        this.showSnackbar('Błąd podczas ładowania danych placówek', 'error');
        this.tenantSummaries = [];
      } finally {
        this.loading = false;
      }
    },

    async switchTenant(tenantId) {
      try {
        this.showSnackbar('Przełączanie placówki...', 'info');
        await tenantService.switchToTenant(tenantId);
        this.showSnackbar('Pomyślnie przełączono placówkę', 'success');

        // Reload the page to refresh all data for the new tenant
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } catch (error) {
        console.error('Error switching tenant:', error);
        this.showSnackbar('Błąd podczas przełączania placówki', 'error');
      }
    },

    showSnackbar(message, color = 'info') {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('pl-PL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },

    formatDateTime(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('pl-PL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    calculateSMSProgress(smsStatus) {
      if (!smsStatus || !smsStatus.smsTotal) return 0;
      return (smsStatus.smsSent / smsStatus.smsTotal) * 100;
    },

    getSMSProgressColor(smsStatus) {
      if (!smsStatus) return 'grey';
      if (smsStatus.showError) return 'error';

      const progress = this.calculateSMSProgress(smsStatus);
      if (progress === 100) return 'success';
      if (progress >= 50) return 'info';
      return 'warning';
    }
  },
  created() {
    this.updateTitle("Dashboard - Podsumowanie Placówek");
    this.loadTenantSummaries();
  },
  mounted() {
    // Auto-refresh every 1 minute (60000 ms)
    this.refreshInterval = setInterval(() => {
      this.loadTenantSummaries();
    }, 60000);
  },
  beforeDestroy() {
    // Clean up the interval when component is destroyed
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }
};
</script>

<style scoped>
.tenant-summary-dashboard {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.dashboard-header {
  margin-bottom: 30px;
  text-align: center;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.tenant-card {
  cursor: pointer;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tenant-card:hover {
  transform: translateY(-4px);
}

.card-header {
  padding: 16px;
  min-height: 80px;
  display: flex;
  align-items: center;
}

.tenant-name {
  font-size: 1.1rem;
  font-weight: 500;
  word-break: break-word;
  display: flex;
  align-items: center;
}

.card-content {
  flex-grow: 1;
  padding: 16px;
}

.card-actions {
  padding: 8px 16px;
  background-color: #f5f5f5;
}

.additional-info {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.stats-section {
  padding: 8px 0;
}

.stats-label {
  display: flex;
  align-items: center;
  font-weight: 500;
}

.stat-box {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 12px 8px;
  text-align: center;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  position: relative;
}

.stat-box:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.stat-box .stat-value {
  font-size: 1.8rem;
  font-weight: bold;
  line-height: 1.2;
}

.stat-new {
  border-left-color: #2196F3;
}

.stat-new .stat-value {
  color: #2196F3;
}

.stat-confirmed {
  border-left-color: #9C27B0;
}

.stat-confirmed .stat-value {
  color: #9C27B0;
}

.stat-finished {
  border-left-color: #4CAF50;
}

.stat-finished .stat-value {
  color: #4CAF50;
}

.stat-canceled {
  border-left-color: #FF5252;
}

.stat-canceled .stat-value {
  color: #FF5252;
}

.warning-icon {
  position: absolute;
  bottom: 4px;
  right: 4px;
}

.global-legend-container {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 8px;
  max-width: 600px;
  margin: 0 auto;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  display: inline-block;
}

.legend-new {
  background-color: #2196F3;
}

.legend-confirmed {
  background-color: #9C27B0;
}

.legend-finished {
  background-color: #4CAF50;
}

.legend-canceled {
  background-color: #FF5252;
}

.legend-text {
  font-size: 0.75rem;
  color: #666;
  white-space: nowrap;
}

.sms-section {
  padding: 8px 0;
}

.sms-progress {
  margin: 8px 0;
}

@media (max-width: 600px) {
  .tenant-summary-dashboard {
    padding: 10px;
  }

  .dashboard-header {
    margin-bottom: 20px;
  }

  .stat-box .stat-value {
    font-size: 1.5rem;
  }

  .global-legend-container {
    gap: 12px;
    padding: 8px;
  }

  .legend-text {
    font-size: 0.7rem;
  }
}
</style>
